import axios from 'axios'
import config from '@/config'
import { getToken, clearAllAuthState } from './auth'
import { ElMessage } from 'element-plus'
// eslint-disable-next-line no-unused-vars
import { useUserStore } from '@/stores/user'
// eslint-disable-next-line no-unused-vars
import router from '@/router'

// 清除用户状态 - 使用统一的增强版清理函数
function clearUserState() {
  clearAllAuthState()
  
  // 在开发环境下打印调试信息
  if (process.env.NODE_ENV === 'development') {
    console.debug('[request.js] 用户状态已清除')
  }
}

// 创建axios实例
const service = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 15000, // 缩短超时时间到15秒
  withCredentials: true, // 全局启用带cookie的跨域请求
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// 添加重试配置
service.defaults.retry = 3; // 最大重试次数
service.defaults.retryDelay = 1000; // 重试间隔（毫秒）
service.defaults.shouldRetry = (error) => {
  // 仅对网络类错误或网关类错误进行重试，避免业务500导致长时间等待
  if (error.code === 'ECONNABORTED' || error.code === 'ECONNREFUSED') return true;
  const status = error?.response?.status;
  return status === 502 || status === 503 || status === 504;
};

// 请求拦截器
service.interceptors.request.use(
  async config => {
    // 如果是公共路径，不添加token
    if (config.url.startsWith('/v1/public')) {
      console.log('公共路径请求，不添加token:', config.url)
      return config
    }
    
    const token = getToken()
    
    if (token) {
      // token已经包含Bearer前缀
      config.headers['Authorization'] = token
      console.log('已添加Authorization头:', token.substring(0, 20) + '...')
    } else {
      console.warn('请求未包含token')
    }
    
    // 方案二：不再需要CSRF相关头部
    // 移除可能存在的X-CSRF-Token头部
    if (config.headers['X-CSRF-Token']) {
      delete config.headers['X-CSRF-Token']
      console.log('已移除X-CSRF-Token头部')
    }
    
    // 移除可能存在的X-Skip-Csrf-Check头部
    if (config.headers['X-Skip-Csrf-Check']) {
      delete config.headers['X-Skip-Csrf-Check']
      console.log('已移除X-Skip-Csrf-Check头部')
    }
    
    // 如果请求体中有_csrf字段，移除它
    if (config.data && config.data._csrf) {
      delete config.data._csrf
      console.log('已从请求体中移除_csrf字段')
    }
    
    // 增加调试信息
    if (process.env.NODE_ENV === 'development') {
      console.log(`请求 [${config.method}] ${config.url}`)
      if (config.data) {
        console.log('请求数据:', JSON.stringify(config.data))
      }
    }
    
    return config
  },
  error => {
    console.error('请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 检查是否为文件流类型响应
    const contentType = response.headers['content-type']
    if (contentType && (contentType.includes('application/octet-stream') || contentType.includes('application/vnd.ms-excel') || contentType.includes('application/vnd.openxmlformats-officedocument'))) {
      return response
    }

    const res = response.data

    // 兼容后端直接返回数组的情况（视为成功列表数据）
    if (Array.isArray(res)) {
      return { code: 0, data: { items: res, total: res.length } }
    }
    
    // 特殊处理：针对内部站点令牌接口
    if (response.config && response.config.url && response.config.url.includes('internal-site-token')) {
      console.log('检测到内部站点令牌接口响应:', res);
      // 如果响应直接包含token属性，认为是成功的
      if (res.token) {
        console.log('发现直接返回的token格式，视为成功');
        return {
          code: 200,
          data: res
        };
      }
    }
    
    // 处理业务成功的情况
    if (res.code === 0 || res.code === 200 || res.code === 201 || res.success === true) {
      return res  // 直接返回响应数据
    }
    
    // 处理各种业务错误
    if (res.code === 401 || (res.success === false && res.message && res.message.includes('身份验证'))) {
      ElMessage.error(res.message || '身份验证失败，请重新登录')
      // 清除用户状态并跳转到登录页
      clearUserState()
      
      // 获取当前路径，如果不是登录页则保存
      const currentPath = router.currentRoute.value.fullPath
      if (currentPath !== '/login' && currentPath !== '/') {
        localStorage.setItem('loginRedirect', currentPath)
      }
      
      // 强制重载页面并跳转到登录页 - 这有助于清除可能存在的恶意脚本或状态
      window.location.href = '/login'
      return Promise.reject(new Error(res.message || '身份验证失败'))
    } else if (res.code === 403 || (res.success === false && res.message && res.message.includes('权限'))) {
      ElMessage.error(res.message || '权限不足，无法访问')
      return Promise.reject(new Error(res.message || '权限不足'))
    } else if (res.success === false) {
      // 处理 success: false 格式的错误
      console.error('业务错误详情:', JSON.stringify(res));
      const errorMsg = res.message || '操作失败';
      ElMessage.error(errorMsg);
      return Promise.reject(new Error(errorMsg));
    } else {
      // 其他业务错误 - 添加更详细的日志和错误信息
      console.error('业务错误详情:', JSON.stringify(res));
      
      // 使用更具体的错误消息
      let errorMsg = res.message || '操作失败';
      
      // 尝试从响应中获取更多详细信息
      if (res.data && res.data.message) {
        errorMsg = res.data.message;
      } else if (res.error) {
        errorMsg = res.error;
      }
      
      ElMessage.error(errorMsg);
      return Promise.reject(new Error(errorMsg));
    }
  },
  async error => {
    console.log('HTTP请求错误详情:', error)
    if (error.response) {
      console.log('详细错误信息:', {
        状态码: error.response.status,
        响应状态: error.response.statusText,
        响应数据: error.response.data,
        请求方法: error.config.method,
        请求地址: error.config.url
      })
    }
    
    // 处理后端返回的错误状态码
    if (error.response) {
      const status = error.response.status
      
      // 提前声明变量，避免在case块中声明
      let currentPath;
      
      switch (status) {
        case 401: // 未登录或token已过期
          console.warn('请求未认证，可能是token已过期:', error.config.url)
          
          // 使用已导入的clearAllAuthState函数
          clearAllAuthState() // 使用增强版的清理函数，清除所有cookie和localStorage
          
          ElMessage.error('登录已过期，请重新登录')
          
          // 尝试获取当前路由路径，用于登录后重定向
          try {
            // 使用块级作用域来处理case中的声明
            {
              const fullPath = router.currentRoute.value.fullPath
              if (fullPath !== '/login' && fullPath !== '/') {
                localStorage.setItem('loginRedirect', fullPath)
                console.log('已保存重定向地址:', fullPath)
              }
            }
          } catch (e) {
            console.error('保存重定向地址失败:', e)
          }
          
          // 如果不在登录页，跳转到登录页
          currentPath = router.currentRoute.value.path
          if (currentPath !== '/login') {
            router.push('/login')
          }
          break
          
        case 403: // 权限不足（服务端鉴权不通过）
          {
            const data = error.response.data || {}
            const reason = data.message || data.error || '权限不足，无法访问'
            ElMessage.error(reason)
            // 可选：跳转到403专页
            // if (router.currentRoute.value.path !== '/403') router.push('/403')
          }
          break
          
        case 404: // 资源不存在
          ElMessage.error('请求的资源不存在')
          break
          
        case 500: // 服务器错误
          ElMessage.error('服务器错误，请稍后重试')
          break
          
        default: // 其他错误
          ElMessage.error('请求失败，请稍后重试')
      }
    } else {
      // 网络错误或请求被取消
      ElMessage.error('网络错误，请检查网络连接')
    }
    
    const config = error.config;
    
    // 如果配置不存在或重试次数超过限制，则拒绝请求
    if (!config || !config.retry || config.retryCount >= config.retry) {
      return Promise.reject(error);
    }

    // 判断是否需要重试
    if (!config.shouldRetry(error)) {
      return Promise.reject(error);
    }

    // 设置重试计数
    config.retryCount = config.retryCount || 0;
    config.retryCount += 1;

    // 记录重试信息
    console.log(`请求重试 (${config.retryCount}/${config.retry}):`, config.url);

    // 创建新的Promise用于延迟
    const backoff = new Promise((resolve) => {
      setTimeout(() => {
        console.log('重试请求:', config.url);
        resolve();
      }, config.retryDelay * config.retryCount);
    });

    // 等待延迟后重试请求
    await backoff;
    return service(config);
  }
)

export default service 